// Store/api/locationApi.js
import { createApi } from "@reduxjs/toolkit/query/react";
import { customFetchBase } from "../customFetchBase";

export const locationApi = createApi({
  reducerPath: "locationApi",
  baseQuery: customFetchBase({ url: "location" }),
  endpoints: (builder) => ({
    // ========== Provinces ==========
    getProvinces: builder.query({
      query: (params = {}) => {
        const {
          page = 1,
          limit = 10,
          search,
          isActive,
          sort = "name",
        } = params;

        let url = `/provinces?page=${page}&limit=${limit}&sort=${sort}`;
        if (search) url += `&q=${search}`;
        if (isActive !== undefined) url += `&isActive=${isActive}`;

        return { url, method: "GET" };
      },
    }),

    getProvinceById: builder.query({
      query: (id) => `/provinces/${id}`,
    }),

    createProvince: builder.mutation({
      query: (data) => ({
        url: "/provinces",
        method: "POST",
        body: data,
      }),
    }),

    updateProvince: builder.mutation({
      query: ({ id, data }) => ({
        url: `/provinces/${id}`,
        method: "PATCH",
        body: data,
      }),
    }),

    deleteProvince: builder.mutation({
      query: (id) => ({
        url: `/provinces/${id}`,
        method: "DELETE",
      }),
    }),

    activateProvince: builder.mutation({
      query: (id) => ({
        url: `/provinces/${id}/activate`,
        method: "PATCH",
      }),
    }),

    deactivateProvince: builder.mutation({
      query: (id) => ({
        url: `/provinces/${id}/deactivate`,
        method: "PATCH",
      }),
    }),

    // ========== Cities ==========
    getCities: builder.query({
      query: (params = {}) => {
        const {
          page = 1,
          limit = 10,
          search,
          province,
          isActive,
          sort = "name",
        } = params;

        let url = `/cities?page=${page}&limit=${limit}&sort=${sort}`;
        if (search) url += `&q=${search}`;
        if (province) url += `&province[eq]=${province}`;
        if (isActive !== undefined) url += `&isActive=${isActive}`;

        return { url, method: "GET" };
      },
    }),

    getCitiesByProvince: builder.query({
      query: (id) => ({
        url: `/provinces/${id}/cities`,
        method: "GET",
      }),
    }),

    getCityById: builder.query({
      query: (id) => `/cities/${id}`,
    }),

    createCity: builder.mutation({
      query: (data) => ({
        url: "/cities",
        method: "POST",
        body: data,
      }),
    }),

    updateCity: builder.mutation({
      query: ({ id, data }) => ({
        url: `/cities/${id}`,
        method: "PATCH",
        body: data,
      }),
    }),

    deleteCity: builder.mutation({
      query: (id) => ({
        url: `/cities/${id}`,
        method: "DELETE",
      }),
    }),

    activateCity: builder.mutation({
      query: (id) => ({
        url: `/cities/${id}/activate`,
        method: "PATCH",
      }),
    }),

    deactivateCity: builder.mutation({
      query: (id) => ({
        url: `/cities/${id}/deactivate`,
        method: "PATCH",
      }),
    }),

    // ========== Areas ==========
    getAreas: builder.query({
      query: (params = {}) => {
        const {
          page = 1,
          limit = 10,
          search,
          province,
          city,
          isActive,
          sort = "name",
        } = params;

        let url = `/areas?page=${page}&limit=${limit}&sort=${sort}`;
        if (search) url += `&q=${search}`;
        if (province) url += `&province[eq]=${province}`;
        if (city) url += `&city[eq]=${city}`;
        if (isActive !== undefined) url += `&isActive=${isActive}`;

        return { url, method: "GET" };
      },
    }),

    getAreasByCity: builder.query({
      query: (cityId) => ({
        url: `/cities/${cityId}/areas`,
        method: "GET",
      }),
    }),

    getAreaById: builder.query({
      query: (id) => `/areas/${id}`,
    }),

    createArea: builder.mutation({
      query: (data) => ({
        url: "/areas",
        method: "POST",
        body: data,
      }),
    }),

    updateArea: builder.mutation({
      query: ({ id, data }) => ({
        url: `/areas/${id}`,
        method: "PATCH",
        body: data,
      }),
    }),

    deleteArea: builder.mutation({
      query: (id) => ({
        url: `/areas/${id}`,
        method: "DELETE",
      }),
    }),

    activateArea: builder.mutation({
      query: (id) => ({
        url: `/areas/${id}/activate`,
        method: "PATCH",
      }),
    }),

    deactivateArea: builder.mutation({
      query: (id) => ({
        url: `/areas/${id}/deactivate`,
        method: "PATCH",
      }),
    }),

    // ========== Location Tree ==========
    getLocationTree: builder.query({
      query: () => "/locations/tree",
    }),
  }),
});

// Export hooks
export const {
  // Provinces
  useGetProvincesQuery,
  useGetProvinceByIdQuery,
  useCreateProvinceMutation,
  useUpdateProvinceMutation,
  useDeleteProvinceMutation,
  useActivateProvinceMutation,
  useDeactivateProvinceMutation,
  // Cities
  useGetCitiesQuery,
  useGetCitiesByProvinceQuery,
  useGetCityByIdQuery,
  useCreateCityMutation,
  useUpdateCityMutation,
  useDeleteCityMutation,
  useActivateCityMutation,
  useDeactivateCityMutation,
  // Areas
  useGetAreasQuery,
  useGetAreasByCityQuery,
  useGetAreaByIdQuery,
  useCreateAreaMutation,
  useUpdateAreaMutation,
  useDeleteAreaMutation,
  useActivateAreaMutation,
  useDeactivateAreaMutation,
  // Tree
  useGetLocationTreeQuery,
} = locationApi;
